import { useEffect, useState, type FormEvent } from 'react'
import { ExternalLink, X } from 'lucide-react'
import { api, type ApiError } from '../lib/api'
import { useAuthStore } from '../lib/auth-store'
import { navigate } from '../lib/router'
import { AppShell } from '../components/shell/AppShell'
import { PageHeader } from '../components/shell/PageHeader'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button, ButtonLink } from '../components/ui/button'
import { Dialog } from '../components/ui/dialog'
import { Badge, StatusBox } from '../components/ui/badge'
import { FloatingInput } from '../components/ui/input'
import { CostsSection } from '../components/settings/CostsSection'
import { HetznerSection } from '../components/settings/HetznerSection'
import { MembersSection } from '../components/settings/MembersSection'
import { InterfacePreferencesSection } from '../components/settings/InterfacePreferencesSection'
import { AuditLogSection } from '../components/settings/AuditLogSection'
import { DangerZoneSection } from '../components/settings/DangerZoneSection'

export function SettingsPage() {
  const accessToken = useAuthStore((s) => s.accessToken)
  const tenant = useAuthStore((s) => s.tenant)
  const clear = useAuthStore((s) => s.clear)

  const [hasKey, setHasKey] = useState(false)
  const [hint, setHint] = useState<string | null>(null)
  const [keyInput, setKeyInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  // Anthropic free-build credits (Doable-managed key fallback)
  const [freeBuilds, setFreeBuilds] = useState<{
    remaining: number
    used: number
    costUsd: string
    managedAvailable: boolean
    unlimited: boolean
  } | null>(null)
  const [showAdvancedKey, setShowAdvancedKey] = useState(false)

  // GitLab
  const [hasGitlab, setHasGitlab] = useState(false)
  const [gitlabHint, setGitlabHint] = useState<string | null>(null)
  const [gitlabInput, setGitlabInput] = useState('')
  const [savingGitlab, setSavingGitlab] = useState(false)
  // OAuth-only state
  const [oauthConfigured, setOauthConfigured] = useState(false)
  const [connectingOauth, setConnectingOauth] = useState(false)
  const [showAdvancedPat, setShowAdvancedPat] = useState(false)
  // Banner driven by ?gitlab=connected|error&reason=… on the redirect-back URL.
  const [oauthBanner, setOauthBanner] = useState<
    { kind: 'connected' } | { kind: 'error'; reason: string } | null
  >(null)

  useEffect(() => {
    if (!accessToken) {
      navigate('/login')
      return
    }
    api
      .getWorkspaceSettings(accessToken)
      .then((s) => {
        setHasKey(s.hasApiKey)
        setHint(s.apiKeyHint)
        setHasGitlab(s.hasGitlabToken)
        setGitlabHint(s.gitlabHint)
        setFreeBuilds(s.freeBuilds)
      })
      .catch((err: ApiError) => {
        if (err.status === 401) {
          clear()
          navigate('/login')
          return
        }
        setError(err.title ?? 'Failed to load settings')
      })
      .finally(() => setLoading(false))

    // Query-string banner from the OAuth callback redirect.
    const params = new URLSearchParams(window.location.search)
    const gitlab = params.get('gitlab')
    if (gitlab === 'connected') {
      setOauthBanner({ kind: 'connected' })
    } else if (gitlab === 'error') {
      setOauthBanner({ kind: 'error', reason: params.get('reason') ?? 'unknown error' })
    }
    if (gitlab) {
      // Strip the params from the URL so a refresh doesn't re-trigger the banner.
      const cleanUrl = `${window.location.pathname}${window.location.hash}`
      window.history.replaceState(null, '', cleanUrl)
    }

    // Server-side OAuth configuration — drives whether we show the Connect button.
    api
      .gitlab.status(accessToken)
      .then((s) => setOauthConfigured(s.configured))
      .catch(() => setOauthConfigured(false))
  }, [accessToken, clear])

  async function onSave(e: FormEvent) {
    e.preventDefault()
    if (!accessToken || !keyInput.trim()) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await api.saveApiKey(accessToken, keyInput.trim())
      setHasKey(res.hasApiKey)
      setHint(res.apiKeyHint)
      setKeyInput('')
      setSuccess('API key saved and encrypted.')
    } catch (err) {
      setError(((err as ApiError).detail ?? (err as ApiError).title) ?? 'Failed to save key')
    } finally {
      setSaving(false)
    }
  }

  // Disconnecting a credential is destructive (builds stop / imports stop):
  // it must never happen on a single click. The button opens this dialog.
  const [confirmDisconnect, setConfirmDisconnect] = useState<'anthropic' | 'gitlab' | null>(null)

  async function onDisconnectGitlab() {
    if (!accessToken) return
    setError(null)
    setSuccess(null)
    try {
      await api.gitlab.disconnect(accessToken)
      setHasGitlab(false)
      setGitlabHint(null)
      setSuccess('GitLab disconnected.')
    } catch (err) {
      setError(((err as ApiError).detail ?? (err as ApiError).title) ?? 'Failed to disconnect')
    }
  }

  async function onDelete() {
    if (!accessToken) return
    setError(null)
    setSuccess(null)
    try {
      await api.deleteApiKey(accessToken)
      setHasKey(false)
      setHint(null)
      setSuccess('API key removed.')
    } catch (err) {
      setError(((err as ApiError).detail ?? (err as ApiError).title) ?? 'Failed to remove key')
    }
  }

  if (!accessToken) return null

  return (
    <AppShell crumbs={[{ label: 'Settings' }]}>
      <PageHeader
        eyebrow={tenant?.name ?? 'Workspace'}
        title="Settings"
        description="Costs, integrations, members and preferences for this workspace."
      />

      {/* Costs — read-only summary at the top so users see the full bill
          picture before they go fiddling with the integrations below. */}
      <CostsSection accessToken={accessToken} />

      {/* Anthropic */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Anthropic
            {loading ? null : hasKey ? (
              <Badge variant="success">Connected</Badge>
            ) : (
              <Badge variant="outline">Not connected</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Builds run on Claude. New workspaces get a few free builds on
            Doable's account; after that, connect your own Anthropic key so
            you control billing directly. Your key is encrypted at rest
            (AES-256-GCM) and never logged.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {loading ? (
            <div className="dwlt-skeleton h-12" />
          ) : (
            <>
              {/* Connected state — tenant key */}
              {hasKey && hint && (
                <StatusBox tone="success" className="flex items-center justify-between gap-3">
                  <p>
                    Connected · key <span className="font-mono">{hint}</span>
                    <span className="block text-xs opacity-80">
                      You're on your own Anthropic billing.
                    </span>
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={() => setConfirmDisconnect('anthropic')} className="bg-card">
                    Disconnect
                  </Button>
                </StatusBox>
              )}

              {/* Not-connected state — free credits or call to connect */}
              {!hasKey && freeBuilds && (
                freeBuilds.unlimited ? (
                  <StatusBox tone="volt">
                    <span className="font-semibold">Unlimited builds</span>{' '}
                    on Doable's account ({freeBuilds.used} used so far · $
                    {Number(freeBuilds.costUsd).toFixed(2)} accumulated). Internal
                    / partner workspace.
                  </StatusBox>
                ) : freeBuilds.managedAvailable && freeBuilds.remaining > 0 ? (
                  <StatusBox tone="volt">
                    <span className="font-semibold">
                      {freeBuilds.remaining} free build{freeBuilds.remaining === 1 ? '' : 's'} remaining
                    </span>{' '}
                    on Doable's account. Connect your own key any time to skip
                    the limit and own your billing.
                  </StatusBox>
                ) : freeBuilds.managedAvailable && freeBuilds.remaining === 0 ? (
                  <StatusBox tone="warning">
                    You've used all your free builds. Connect your Anthropic key
                    below to keep going — your account, your costs.
                  </StatusBox>
                ) : (
                  <StatusBox tone="volt">
                    Connect your Anthropic key to enable builds.
                  </StatusBox>
                )
              )}

              {error && <StatusBox tone="destructive">{error}</StatusBox>}
              {success && <StatusBox tone="success">{success}</StatusBox>}

              {/* Connect button — opens Anthropic console in a new tab so the
                  user can grab a key, then they paste below. */}
              {!hasKey && (
                <ButtonLink
                  variant="outline"
                  href="https://console.anthropic.com/settings/keys"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setShowAdvancedKey(true)}
                >
                  Connect Anthropic account <ExternalLink />
                </ButtonLink>
              )}

              {/* Paste form — shown automatically once they click Connect, or
                  when they want to replace an existing key. */}
              <details
                className="rounded-lg border"
                open={showAdvancedKey || !hasKey}
              >
                <summary className="select-none px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {hasKey ? 'Replace key' : 'Paste API key'}
                </summary>
                <form onSubmit={onSave} className="space-y-3 p-3 pt-1">
                  <FloatingInput
                    label="Anthropic API key"
                    type="password"
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    placeholder="sk-ant-api03-…"
                    className="font-mono"
                    autoComplete="off"
                    hint={
                      <>
                        Get one at{' '}
                        <a
                          href="https://console.anthropic.com/settings/keys"
                          target="_blank"
                          rel="noreferrer"
                          className="text-volt-ink underline underline-offset-2"
                        >
                          console.anthropic.com/settings/keys
                        </a>
                        . Saved keys are validated against Anthropic before being stored.
                      </>
                    }
                  />
                  <Button type="submit" disabled={saving || !keyInput.trim()}>
                    {saving ? 'Validating…' : hasKey ? 'Replace key' : 'Save key'}
                  </Button>
                </form>
              </details>
            </>
          )}
        </CardContent>
      </Card>

      {/* GitLab section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            GitLab integration
            {hasGitlab ? (
              <Badge variant="success">Connected</Badge>
            ) : (
              <Badge variant="outline">Not connected</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Connect a GitLab account to import existing repos into Doable and push your
            changes back as merge requests.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {oauthBanner?.kind === 'connected' && (
            <StatusBox tone="success" className="flex items-start justify-between gap-3">
              <p>✓ GitLab connected. You can now import repos from your dashboard.</p>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setOauthBanner(null)}
                aria-label="Dismiss"
                className="-my-1 text-success-ink"
              >
                <X />
              </Button>
            </StatusBox>
          )}
          {oauthBanner?.kind === 'error' && (
            <StatusBox tone="warning" className="flex items-start justify-between gap-3">
              <p>
                Connection failed: <span className="font-mono">{oauthBanner.reason}</span>
              </p>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setOauthBanner(null)}
                aria-label="Dismiss"
                className="-my-1 text-warning-ink"
              >
                <X />
              </Button>
            </StatusBox>
          )}

          {hasGitlab && gitlabHint && (
            <StatusBox tone="success" className="flex items-center justify-between gap-3">
              <p>
                Connected · token <span className="font-mono">{gitlabHint}</span>
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="bg-card"
                onClick={() => setConfirmDisconnect('gitlab')}
              >
                Disconnect
              </Button>
            </StatusBox>
          )}

          {!hasGitlab && oauthConfigured && (
            <Button
              type="button"
              onClick={async () => {
                if (!accessToken) return
                setConnectingOauth(true)
                setError(null)
                try {
                  const { authorizeUrl } = await api.gitlab.initiate(accessToken)
                  window.location.href = authorizeUrl
                } catch (err) {
                  setError(((err as ApiError).detail ?? (err as ApiError).title) ?? 'Failed to start GitLab connection')
                  setConnectingOauth(false)
                }
              }}
              disabled={connectingOauth}
            >
              {connectingOauth ? 'Redirecting to GitLab…' : 'Connect GitLab account'}
            </Button>
          )}
          {!hasGitlab && !oauthConfigured && (
            <StatusBox tone="warning">
              OAuth is not configured on this server — use the manual token below.
            </StatusBox>
          )}

          {/* Manual PAT — collapsed by default once OAuth is available, expanded
              automatically if the user is on a server without OAuth configured. */}
          <details
            className="rounded-lg border"
            open={!oauthConfigured && !hasGitlab}
            onToggle={(e) => setShowAdvancedPat((e.target as HTMLDetailsElement).open)}
          >
            <summary className="select-none px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {showAdvancedPat ? '−' : '+'} Advanced: paste a personal access token
            </summary>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (!accessToken || !gitlabInput.trim()) return
                setSavingGitlab(true)
                setError(null)
                setSuccess(null)
                try {
                  const res = await api.saveGitlabToken(accessToken, gitlabInput.trim())
                  setHasGitlab(res.hasGitlabToken)
                  setGitlabHint(res.gitlabHint)
                  setGitlabInput('')
                  setSuccess('GitLab token saved.')
                } catch (err) {
                  setError(((err as ApiError).detail ?? (err as ApiError).title) ?? 'Failed to save token')
                } finally {
                  setSavingGitlab(false)
                }
              }}
              className="space-y-3 p-3 pt-1"
            >
              <FloatingInput
                label="GitLab personal access token"
                type="password"
                value={gitlabInput}
                onChange={(e) => setGitlabInput(e.target.value)}
                placeholder="glpat-..."
                className="font-mono"
                autoComplete="off"
                hint={
                  <>
                    Create one at{' '}
                    <a
                      href="https://gitlab.com/-/user_settings/personal_access_tokens"
                      target="_blank"
                      rel="noreferrer"
                      className="text-volt-ink underline underline-offset-2"
                    >
                      GitLab → Settings → Access Tokens
                    </a>{' '}
                    with <code className="rounded-md bg-muted px-1 py-0.5 font-mono text-[10px]">api</code> scope.
                  </>
                }
              />
              <Button
                type="submit"
                variant={oauthConfigured ? 'outline' : 'default'}
                disabled={savingGitlab || !gitlabInput.trim()}
              >
                {savingGitlab ? 'Saving…' : hasGitlab ? 'Replace token' : 'Save token'}
              </Button>
            </form>
          </details>
        </CardContent>
      </Card>

      {/* Hetzner section */}
      <HetznerSection accessToken={accessToken} />

      {/* Members + invites — admin+ can mutate, everyone can read */}
      <MembersSection accessToken={accessToken} />

      {/* Interface preferences — browser-local */}
      <InterfacePreferencesSection />

      {/* Audit log */}
      <AuditLogSection accessToken={accessToken} />

      {/* Danger zone — owner-only */}
      {tenant?.role === 'owner' && (
        <DangerZoneSection
          accessToken={accessToken}
          tenantSlug={tenant.slug}
          tenantName={tenant.name}
          onDeleted={() => {
            clear()
            navigate('/login')
          }}
        />
      )}

      <p className="text-center text-xs text-muted-foreground">
        Workspace: {tenant?.name ?? '—'} ({tenant?.slug ?? '—'})
      </p>
      <Dialog
        open={confirmDisconnect !== null}
        onClose={() => setConfirmDisconnect(null)}
        title={confirmDisconnect === 'anthropic' ? 'Disconnect Anthropic?' : 'Disconnect GitLab?'}
        description={
          confirmDisconnect === 'anthropic'
            ? 'The stored API key is deleted. Builds stop until a key is connected again.'
            : 'The stored token is removed. Imports, repo auto-create and exports stop until you reconnect.'
        }
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setConfirmDisconnect(null)}>Cancel</Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                const which = confirmDisconnect
                setConfirmDisconnect(null)
                if (which === 'anthropic') void onDelete()
                if (which === 'gitlab') void onDisconnectGitlab()
              }}
            >
              Disconnect
            </Button>
          </>
        }
      />
    </AppShell>
  )
}
